import {
  from,
  ApolloError,
  ApolloClient,
  useApolloClient as useProviderClient,
} from '@apollo/client'
import { ErrorResponse, onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'

import settings from 'shared/common/settings'
import { getClientSession } from 'shared/utils/auth/v2/client-session'
import { isServerError, isString } from 'shared/utils/type-guards'

import cache from './cache'
import { createClientLinks } from './links/client'

import logger from '../logger'

type AuthLinkSetterResult = Promise<{
  headers?: {
    authorization: string
  }
}>

const errorLink = onError(logError)

export function logError({
  graphQLErrors,
  networkError,
  operation,
}: ErrorResponse): void {
  const operationName = operation?.operationName

  if (graphQLErrors?.length) {
    // technically this error logging should be a loop...
    const mainError = graphQLErrors[0]

    if (!mainError?.message) {
      return
    }

    const ignoredErrorMessages = [
      'Missing time entries',
      'Invalid Credentials',
      'Resource Not Found',
      'Not Authorized',
      'Forbidden',
      'Project not found',
      'ActiveRecord::RecordNotFound',
    ]

    if (
      ignoredErrorMessages.some((ignoredErrorMessage) =>
        mainError.message.includes(ignoredErrorMessage),
      )
    ) {
      return
    }

    const error = new ApolloError({ graphQLErrors, networkError })

    logger.error(error, {
      key: 'graphQLError',
      tags: { graphqlOperation: operationName },
      extra: { graphQLErrors: JSON.stringify(graphQLErrors) },
    })
  }

  if (networkError) {
    if (
      isServerError(networkError) &&
      [401, 403].includes(networkError.statusCode)
    ) {
      return
    }

    const error = new ApolloError({
      graphQLErrors: graphQLErrors || [],
      networkError,
    })

    logger.error(error, {
      key: 'networkError',
      tags: { graphqlOperation: operationName },
      extra: { networkError: JSON.stringify(networkError) },
    })
  }
}

export const authLinkSetter = (): AuthLinkSetterResult =>
  new Promise((success) => {
    // Always use getClientSession() which has proper caching, refresh logic, and synchronization
    // This eliminates race conditions with sessionStorage token updates
    return getClientSession()
      .then((data) => {
        const accessToken = data?.accessToken

        if (isString(accessToken)) {
          return success({
            headers: {
              authorization: `Bearer ${accessToken}`,
            },
          })
        }

        // Fallback for when there is no valid token available
        // Note: This will cause a 401 response, but the error link will
        // attempt to refresh the token and retry the request
        logger.debug('No access token available for Apollo request')
        return success({})
      })
      .catch((error) => {
        logger.error(
          `error fetching bearer token for apollo: ${error.message}`,
          {
            key: 'apollo-auth-error',
            extra: {
              error: JSON.stringify(error),
            },
          },
        )
        // Return empty headers - will trigger 401, which will be caught by error link
        return success({})
      })
  })
/**
 * Designed to be used on the client-side, paired with a SessionProvider
 * who is already keeping the session up to date and providing valid
 * access tokens.
 *
 * This client WILL automatically retry failed requests on 401 errors by:
 * 1. Clearing the session cache
 * 2. Forcing a fresh token fetch
 * 3. Retrying the request once with the new token
 * 4. Only logging out if the retry also fails or refresh fails with auth error
 */
export const createClient = (version = '1.0'): ApolloClient<object> => {
  return new ApolloClient({
    cache,
    connectToDevTools: settings.appEnv !== 'production',
    queryDeduplication: true,
    link: from([
      errorLink,
      setContext(authLinkSetter),
      setContext((_, ctx) => ({
        headers: { ...ctx.headers, 'x-graphql-version': version },
      })),
      createClientLinks(settings.getValue('API_URI')),
    ]),
  })
}

/**
 * React Hook for accessing the Apollo Client from the ApolloSessionProvider
 */
export const useApolloClient = (): ApolloClient<object> => {
  return useProviderClient()
}
